import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Input, Upload, message, Progress, Collapse, Card, Statistic, Row, Col, Tag, Modal, Form, Space, Select, Switch, Tooltip } from 'antd';
import { SendOutlined, PaperClipOutlined, FileOutlined, UploadOutlined, DownloadOutlined, BarChartOutlined, ExportOutlined, EditOutlined, HistoryOutlined, ImportOutlined, LoadingOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext.jsx';
import apiService from '../services/apiService';
import logo from '../assets/APPlyzerLogoHead.svg';
import ReactMarkdown from 'react-markdown';
import ScorecardBuckets from './ScorecardBuckets.jsx';
import ComplianceMatches from './ComplianceMatches.jsx';
import OverallSummary from './OverallSummary.jsx';
import RulingConfigIndicator from './RulingConfigIndicator.jsx';
import AppActionsHistory from './AppActionsHistory.jsx';
import CompactScorecardSelector from './CompactScorecardSelector.jsx';
import { validateScorecardData, isLegacyScorecardData } from '../utils/scorecardValidation.js';

const { TextArea } = Input;
const { Dragger } = Upload;



const ChatInterface = ({ sessionId, onSessionChange, currentProject, onSessionUpdate, onSidebarRefresh }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(null);
  const [taskStatuses, setTaskStatuses] = useState({});
  const [sessionSummary, setSessionSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [sessionName, setSessionName] = useState('');
  const [isNameModalVisible, setIsNameModalVisible] = useState(false);
  const [isActionsHistoryVisible, setIsActionsHistoryVisible] = useState(false);
  const [selectedScorecards, setSelectedScorecards] = useState([]);
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [availableSessions, setAvailableSessions] = useState([]);
  const [nameForm] = Form.useForm();
  const [importForm] = Form.useForm();
  const messagesEndRef = useRef(null);
  const { getAuthToken } = useAuth();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadChatHistory = useCallback(async () => {
    try {
      const token = getAuthToken();
      let response;
      
      try {
        // First try to load without archived sessions
        response = await apiService.getChatHistory(
          token, 
          50, 
          sessionId, 
          currentProject?.id,
          false
        );
      } catch (error) {
        // If that fails, try including archived sessions
        response = await apiService.getChatHistory(
          token, 
          50, 
          sessionId, 
          currentProject?.id,
          true
        );
        
        // Show a notice that this is an archived session
        message.warning('This session is archived. You can only view the history.', 5);
      }
      
      // Process history to add download info from metadata and filter out empty messages
      const processedHistory = (response.history || [])
        .map(msg => {
          if (msg.metadata && msg.metadata.download_url && msg.metadata.output_file) {
            return {
              ...msg,
              downloadUrl: msg.metadata.download_url,
              outputFile: msg.metadata.output_file
            };
          }
          return msg;
        })
        .filter(msg => {
          // Keep system messages and app actions even if they have empty message text
          if (msg.system_message || msg.message_type === 'app_action') {
            return true;
          }
          
          // For regular chat messages, filter out entries where both message and response are empty
          const hasMessage = msg.message && msg.message.trim().length > 0;
          const hasResponse = msg.response && msg.response.trim().length > 0;
          
          return hasMessage || hasResponse;
        });
      
      setMessages(processedHistory);
    } catch (error) {
      console.error('Failed to load chat history:', error);
      message.error('Failed to load chat history');
    }
  }, [sessionId, currentProject?.id, getAuthToken]);

  const loadSessionInfo = useCallback(async () => {
    try {
      const token = getAuthToken();
      const response = await apiService.getSessions(token, currentProject?.id);
      
      // Find the current session in the list
      const currentSession = response.sessions?.find(session => session.session_id === sessionId);
      if (currentSession && currentSession.name) {
        setSessionName(currentSession.name);
      } else {
        setSessionName('');
      }
    } catch (error) {
      console.error('Failed to load session info:', error);
      // Don't show error message as this is optional functionality
    }
  }, [sessionId, currentProject?.id, getAuthToken]);

  useEffect(() => {
    if (sessionId) {
      loadChatHistory();
      loadSessionInfo();
    } else {
      setMessages([]);
      setSessionName('');
    }
  }, [sessionId, currentProject, loadChatHistory, loadSessionInfo]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || loading) return;

    const messageText = inputValue.trim();
    setInputValue('');
    setLoading(true);

    // Add user message immediately
    const userMessage = {
      id: Date.now(),
      message: messageText,
      timestamp: new Date().toISOString(),
      isUser: true
    };
    setMessages(prev => [...prev, userMessage]);

    try {
      const token = getAuthToken();
      const response = await apiService.sendChatMessage(
        messageText, 
        token, 
        sessionId, 
        currentProject?.id
      );
      
      // Add assistant response
      const assistantMessage = {
        id: response.chat_id,
        message: messageText,
        response: response.response,
        timestamp: response.timestamp,
        session_id: response.session_id,
        project_id: response.project_id || currentProject?.id,
        isUser: false
      };
      
      setMessages(prev => [...prev, assistantMessage]);

      // Update session if changed
      if (onSessionChange && response.session_id !== sessionId) {
        onSessionChange(response.session_id);
      }

    } catch (error) {
      console.error('Failed to send message:', error);
      message.error('Failed to send message');
      // Remove the user message on error
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleFileUpload = async (file) => {
    setUploadingFile({ name: file.name, progress: 0 });
    
    try {
      const token = getAuthToken();
      const scorecardNumbers = selectedScorecards.length > 0 ? selectedScorecards.join(',') : null;
      
      const response = await apiService.uploadFile(
        file, 
        token, 
        'app_analysis', 
        currentProject?.id,
        sessionId,
        scorecardNumbers
      );
      
      setUploadingFile({ 
        name: file.name, 
        progress: 0, 
        status: 'uploaded',
        statusText: 'Queued for analysis...'
      });
      
      // Start polling for task status
      pollTaskStatus(response.task_id, file.name);
      
      message.success('File uploaded successfully! Processing will begin shortly.');
      
      // Handle session creation/update
      let isNewSession = false;
      if (response.session_id) {
        // If no current session or a new session was created, update the session
        if (!sessionId || (sessionId !== response.session_id)) {
          isNewSession = true;
          console.log('New session created:', response.session_id);
          console.log('onSidebarRefresh available:', !!onSidebarRefresh);
          
          if (onSessionChange) {
            onSessionChange(response.session_id);
          }
          
          // Show success notification for new session creation
          message.info({
            content: `New compliance session created: ${response.session_id.slice(0, 12)}...`,
            duration: 3,
          });
        }
        
        // Refresh sidebar to show updated session list
        console.log('Attempting to refresh sidebar, callback available:', !!onSidebarRefresh);
        if (onSidebarRefresh) {
          // Delay to ensure the session is fully created on the backend
          setTimeout(() => {
            console.log('Calling onSidebarRefresh now');
            onSidebarRefresh();
          }, 1000); // Increased delay to 1 second to ensure backend processing
        } else {
          console.warn('onSidebarRefresh callback not available');
        }
      }
      
      // Add system message about file upload
      const sessionInfo = isNewSession && response.session_id ? 
        ` A new compliance session (${response.session_id.slice(0, 12)}...) has been created for this analysis.` : '';
      
      const systemMessage = {
        id: Date.now(),
        message: `File "${file.name}" has been uploaded and queued for processing.${sessionInfo}`,
        timestamp: new Date().toISOString(),
        isSystem: true,
        taskId: response.task_id
      };
      setMessages(prev => [...prev, systemMessage]);

    } catch (error) {
      console.error('File upload failed:', error);
      message.error(`Upload failed: ${error.message}`);
      setUploadingFile(null);
    }

    return false; // Prevent default upload behavior
  };

  const pollTaskStatus = async (taskId, fileName) => {
    const token = getAuthToken();
    
    const poll = async () => {
      try {
        const response = await apiService.getTaskStatus(taskId, token);
        
        setTaskStatuses(prev => ({
          ...prev,
          [taskId]: response
        }));

        if (response.status === 'SUCCESS') {
          // Update upload status to completed
          setUploadingFile(prev => prev ? { 
            ...prev, 
            progress: 100, 
            status: 'completed',
            statusText: 'Analysis completed!'
          } : null);
          
          // Add completion message (without full analysis result)
          const completionMessage = {
            id: Date.now(),
            message: `Analysis of "${fileName}" completed successfully!`,
            response: `File "${fileName}" has been processed and analyzed. Analysis results are now available.`,
            timestamp: new Date().toISOString(),
            isSystem: true,
            downloadUrl: response.result?.download_url,
            outputFile: response.result?.output_file,
          };
          setMessages(prev => [...prev, completionMessage]);
          
          // Refresh session summary and sidebar to show updated data
          if (sessionId) {
            loadSessionSummary();
          }
          if (onSidebarRefresh) {
            onSidebarRefresh();
          }
          
          // Auto-dismiss after completion
          setTimeout(() => {
            setUploadingFile(null);
          }, 5000);
          
        } else if (response.status === 'FAILURE') {
          setUploadingFile(prev => prev ? { 
            ...prev, 
            status: 'failed',
            statusText: 'Processing failed'
          } : null);
          message.error(`Processing failed: ${response.error}`);
          
          // Auto-dismiss after failure
          setTimeout(() => {
            setUploadingFile(null);
          }, 8000);
          
        } else if (response.status === 'PROGRESS') {
          // Update progress information
          if (response.progress) {
            const { current, total, percentage, status } = response.progress;
            setUploadingFile(prev => prev ? { 
              ...prev, 
              progress: percentage || 0,
              status: 'processing',
              statusText: `${current}/${total} apps processed`
            } : null);
          }
          // Continue polling
          setTimeout(poll, 2000); // Poll more frequently during processing
          
        } else if (response.status === 'PENDING') {
          // Update to show initializing state
          setUploadingFile(prev => prev ? { 
            ...prev, 
            progress: 0,
            status: 'initializing',
            statusText: 'Initializing...'
          } : null);
          // Continue polling
          setTimeout(poll, 3000);
        }
      } catch (error) {
        console.error('Failed to check task status:', error);
        setTimeout(poll, 5000); // Retry after longer delay
      }
    };

    poll();
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };



  // Export session functionality
  const handleExportSession = async () => {
    if (!sessionId) {
      message.warning('No active session to export');
      return;
    }

    try {
      setExportLoading(true);
      const token = getAuthToken();
      const response = await apiService.exportChatSession(sessionId, token, currentProject?.id);
      
      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Extract filename from Content-Disposition header
      const contentDisposition = response.headers.get('content-disposition');
      let filename = 'chat_export.csv';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      message.success('Chat session exported successfully!');
    } catch (error) {
      console.error('Failed to export session:', error);
      message.error('Failed to export chat session');
    } finally {
      setExportLoading(false);
    }
  };

  // Session naming functionality
  const handleSetSessionName = () => {
    if (!sessionId) {
      message.warning('No active session to name');
      return;
    }
    nameForm.setFieldsValue({ sessionName: sessionName || '' });
    setIsNameModalVisible(true);
  };

  const handleSessionNameSubmit = async (values) => {
    try {
      const token = getAuthToken();
      await apiService.setSessionName(sessionId, values.sessionName, token, currentProject?.id);
      setSessionName(values.sessionName);
      setIsNameModalVisible(false);
      nameForm.resetFields();
      message.success('Session name updated successfully!');
      
      // Notify parent component to update session list
      if (onSessionUpdate) {
        onSessionUpdate(sessionId, values.sessionName);
      }
    } catch (error) {
      console.error('Failed to set session name:', error);
      message.error(error.message || 'Failed to update session name');
    }
  };

  const validateSessionName = (rule, value) => {
    if (!value || value.trim().length === 0) {
      return Promise.reject('Session name is required');
    }
    if (value.trim().length > 100) {
      return Promise.reject('Session name must be 100 characters or less');
    }
    return Promise.resolve();
  };

  // Import apps functionality
  const loadAvailableSessions = useCallback(async () => {
    try {
      const token = getAuthToken();
      const response = await apiService.getSessions(token, currentProject?.id);
      
      // Filter out current session and add record counts (mock for now)
      const sessions = response.sessions
        ?.filter(session => !sessionId || session.session_id !== sessionId)
        ?.map(session => ({
          session_id: session.session_id,
          session_name: session.session_name || `Session ${session.session_id?.slice(0, 8)}`,
          record_count: session.message_count || 0, // Use message count as proxy for record count
          last_updated: session.updated_at || session.created_at
        })) || [];
      
      setAvailableSessions(sessions);
    } catch (error) {
      console.error('Failed to load sessions:', error);
      setAvailableSessions([]);
    }
  }, [getAuthToken, currentProject?.id, sessionId]);

  const handleImportApps = () => {
    if (!currentProject) {
      message.warning('Please select a project first');
      return;
    }
    loadAvailableSessions();
    setIsImportModalVisible(true);
  };

  const handleImportSubmit = async (values) => {
    setImportLoading(true);
    
    try {
      const token = getAuthToken();
      
      // If no sessionId, we need to create a new session or handle the import differently
      let targetSessionId = sessionId;
      if (!targetSessionId) {
        // For new compliance sessions, we'll let the backend handle session creation
        // or we could create a new session here
        message.info('Creating new session for import...');
      }
      
      const result = await apiService.importSessionApps(
        values.sourceSessionId,
        targetSessionId || 'new', // Use 'new' as placeholder if no sessionId
        token,
        currentProject?.id,
        values.overwriteDuplicates || false
      );

      if (result.success) {
        message.success(
          `Successfully imported ${result.imported_count} apps! ` +
          `${result.updated_count} updated, ${result.skipped_count} skipped.`
        );
        setIsImportModalVisible(false);
        importForm.resetFields();
        
        // If we didn't have a sessionId before, update it with the result
        if (!sessionId && result.target_session_id) {
          if (onSessionChange) {
            onSessionChange(result.target_session_id);
          }
        }
        
        // Refresh chat history to show imported apps
        await loadChatHistory();
      } else {
        message.error(`Import failed: ${result.message}`);
      }
    } catch (error) {
      console.error('Import error:', error);
      message.error('Failed to import apps. Please try again.');
    } finally {
      setImportLoading(false);
    }
  };

  const renderScorecardDashboard = (data) => {
    const isNewLogic = data.new_logic && !isLegacyScorecardData(data);
    
    if (isNewLogic) {
      // Render new bucket system dashboard
      return (
        <div>
          <RulingConfigIndicator
            projectId={currentProject?.id}
            sessionSummaryData={data}
            style={{ marginBottom: 16 }}
          />
          
          {/* Always show Overall Analysis Results */}
          <OverallSummary 
            summary={data.overall_summary}
            totalApps={data.total_apps}
            appsNeedingReview={data.apps_needing_review}
            isNewLogic={true}
            sessionId={sessionId}
            projectId={currentProject?.id}
            sessionData={data}
          />
          
          {/* Make Scorecard Details expandable */}
          <div style={{ marginBottom: 16 }}>
            <Collapse
              size="small"
              items={[
                {
                  key: 'scorecard-details',
                  label: (
                    <span style={{ 
                      color: '#ffffff', 
                      fontWeight: '600',
                      fontSize: '14px'
                    }}>
                      Scorecard Details
                    </span>
                  ),
                  children: (
                    <div>
                      <Row gutter={[16, 12]}>
                        <Col span={12}>
                          <ScorecardBuckets 
                            scorecard="1" 
                            data={data.summary_by_scorecard.scorecard_1} 
                          />
                        </Col>
                        <Col span={12}>
                          <ScorecardBuckets 
                            scorecard="2" 
                            data={data.summary_by_scorecard.scorecard_2} 
                          />
                        </Col>
                      </Row>
                      
                      <Row gutter={[16, 12]} style={{ marginTop: 12 }}>
                        <Col span={12}>
                          <ComplianceMatches 
                            scorecard="3" 
                            data={data.summary_by_scorecard.scorecard_3} 
                          />
                        </Col>
                        <Col span={12}>
                          <ComplianceMatches 
                            scorecard="4" 
                            data={data.summary_by_scorecard.scorecard_4} 
                          />
                        </Col>
                      </Row>
                    </div>
                  )
                }
              ]}
            />
          </div>
        </div>
      );
    } else {
      // Render legacy pass/refer/fail system
      return (
        <div>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={6}>
              <Card 
                size="small"
                style={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}
              >
                <Statistic
                  title={<span style={{ color: '#e2e8f0', fontSize: '13px', fontWeight: '500' }}>Total Apps</span>}
                  value={data.total_apps}
                  valueStyle={{ color: '#ffffff', fontSize: '22px', fontWeight: '700' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card 
                size="small"
                style={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}
              >
                <Statistic
                  title={<span style={{ color: '#e2e8f0', fontSize: '13px', fontWeight: '500' }}>Need Review</span>}
                  value={data.apps_needing_review}
                  suffix={<span style={{ color: '#f97316', fontSize: '14px' }}>({data.apps_needing_review_percentage?.toFixed(1)}%)</span>}
                  valueStyle={{ color: '#f97316', fontSize: '20px', fontWeight: '700' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card 
                size="small"
                style={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}
              >
                <Statistic
                  title={<span style={{ color: '#e2e8f0', fontSize: '13px', fontWeight: '500' }}>Pass Rate</span>}
                  value={data.overall_summary?.pass_rate?.toFixed(1)}
                  suffix={<span style={{ color: '#22c55e', fontSize: '16px' }}>%</span>}
                  valueStyle={{ color: '#22c55e', fontSize: '20px', fontWeight: '700' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card 
                size="small"
                style={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}
              >
                <Statistic
                  title={<span style={{ color: '#e2e8f0', fontSize: '13px', fontWeight: '500' }}>Fail Rate</span>}
                  value={data.overall_summary?.fail_rate?.toFixed(1)}
                  suffix={<span style={{ color: '#ef4444', fontSize: '16px' }}>%</span>}
                  valueStyle={{ color: '#ef4444', fontSize: '20px', fontWeight: '700' }}
                />
              </Card>
            </Col>
          </Row>
          
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ color: '#ffffff', marginBottom: 12, fontWeight: '600' }}>
              Scorecard Breakdown
              <Tag 
                size="small" 
                color="orange"
                style={{ marginLeft: 8, fontSize: '10px', fontWeight: '500' }}
              >
                Legacy System
              </Tag>
            </h4>
            <Row gutter={[16, 8]}>
              {Object.entries(data.summary_by_scorecard || {}).map(([scorecard, scorecardData]) => {
                const scorecardNum = parseInt(scorecard.split('_')[1]);
                const isMatchBased = scorecardNum === 3 || scorecardNum === 4;
                const scoringType = isMatchBased ? 'match-based' : 'numeric';
                
                return (
                  <Col span={12} key={scorecard}>
                    <Card 
                      size="small" 
                      title={
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                          <span style={{ color: '#ffffff', fontWeight: '600', fontSize: '14px' }}>
                            {scorecard.replace('_', ' ').toUpperCase()}
                          </span>
                          <Tag 
                            size="small" 
                            color={isMatchBased ? 'purple' : 'blue'}
                            style={{ fontSize: '10px', fontWeight: '500' }}
                          >
                            {scoringType}
                          </Tag>
                        </div>
                      }
                      style={{ 
                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', fontWeight: '600' }}>
                        <span style={{ color: '#22c55e' }}>Pass: {scorecardData.pass}</span>
                        <span style={{ color: '#f97316' }}>Refer: {scorecardData.refer}</span>
                        <span style={{ color: '#ef4444' }}>Fail: {scorecardData.fail}</span>
                        <span style={{ color: '#94a3b8' }}>Errors: {scorecardData.errors}</span>
                      </div>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </div>
        </div>
      );
    }
  };

  const getDisplaySessionName = () => {
    if (sessionName) {
      return sessionName;
    }
    if (sessionId) {
      return `Session ${sessionId.slice(0, 8)}`;
    }
    return 'Current Session';
  };

  const loadSessionSummary = useCallback(async () => {
    try {
      setSummaryLoading(true);
      const token = getAuthToken();
      
      // Try new API first, fall back to legacy if needed
      let response;
      try {
        response = await apiService.getNewSessionSummary(sessionId, token, currentProject?.id);
        console.log('Successfully loaded new scorecard system data');
      } catch (newApiError) {
        console.log('New API not available, falling back to legacy system');
        response = await apiService.getSessionSummary(sessionId, token, currentProject?.id);
      }
      
      // Validate the response if it claims to use new logic
      if (response.new_logic && !validateScorecardData(response)) {
        console.warn('Invalid new scorecard data structure, treating as legacy');
        response.new_logic = false;
      }
      
      setSessionSummary(response);
    } catch (error) {
      console.error('Failed to load session summary:', error);
      // Don't show error for missing summary as it's optional
    } finally {
      setSummaryLoading(false);
    }
  }, [sessionId, currentProject?.id, getAuthToken]);

  // Load session summary when session changes
  useEffect(() => {
    if (sessionId && currentProject) {
      loadSessionSummary();
    }
  }, [sessionId, currentProject, loadSessionSummary]);



  const renderMessage = (msg) => {
    if (msg.isSystem || msg.system_message) {
      return (
        <div key={msg.id} className="message system">
          <div className="message-content">
            <div className="message-bubble" style={{ 
              background: 'var(--surface-glass)', 
              border: '1px solid var(--accent-primary)',
              color: 'var(--text-primary)',
              backdropFilter: 'blur(10px)'
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <FileOutlined style={{ color: 'var(--accent-primary)', marginTop: '2px', flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <ReactMarkdown>{msg.message}</ReactMarkdown>
                </div>
              </div>
              {msg.response && (
                <div style={{ marginTop: '12px', fontSize: '14px', opacity: 0.9, lineHeight: '1.5' }}>
                  <ReactMarkdown>{msg.response}</ReactMarkdown>
                </div>
              )}
              {msg.downloadUrl && (
                <div style={{ marginTop: '12px' }}>
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                   
                    style={{
                      background: 'linear-gradient(135deg, var(--accent-primary), var(--accent-secondary))',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '600'
                    }}
                  >
                    <a href={msg.downloadUrl} style={{ color: 'inherit', textDecoration: 'none' }}>
                      {msg.outputFile || 'analyzed_file.xlsx'}
                    </a>
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div key={msg.id} className={`message ${msg.isUser ? 'user' : 'assistant'}`}>
        <div className="message-avatar">
          {msg.isUser ? 'U' : 'AI'}
        </div>
        <div className="message-content">
          <div className="message-bubble">
            <ReactMarkdown>
              {msg.isUser ? msg.message : msg.response || msg.message}
            </ReactMarkdown>
          </div>
          <div className="message-time">
            {formatTime(msg.timestamp)}
          </div>
        </div>
      </div>
    );
  };

  const WelcomeScreen = () => (
    <div className="welcome-screen">
      <img src={logo} alt="APPlyzer" className="logo" />
      <h2>Hello! I'm your compliance assistant.</h2>
      <p>Upload a document using the area above or ask any compliance-related questions using my research assistant below.</p>
    </div>
  );

  // Show project requirement notice if no project is selected
  if (!currentProject) {
    return (
      <div className="chat-container">
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          height: '100%',
          flexDirection: 'column',
          gap: '16px',
          padding: '40px',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '48px',
            opacity: 0.3,
            marginBottom: '16px'
          }}>
            📁
          </div>
          <h3 style={{ 
            margin: 0, 
            color: '#6b7280',
            fontSize: '18px',
            fontWeight: '500'
          }}>
            Please select a project to start chatting
          </h3>
          <p style={{ 
            margin: 0, 
            color: '#9ca3af',
            fontSize: '14px',
            maxWidth: '300px',
            lineHeight: '1.5'
          }}>
            All chat conversations must be associated with a project. Please select or create a project from the dropdown above.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-container">
      {/* Session Name Header */}
      {sessionId && (
        <div
          style={{
            padding: "12px 16px",
            borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
            backgroundColor: "rgba(255, 255, 255, 0.03)",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: "12px",
          }}
        >
          <div style={{ flex: 1 }}>
            <h3
              style={{
                margin: 0,
                color: "#ffffff",
                fontSize: "16px",
                fontWeight: "600",
                display: "flex",
                alignItems: "flex-start",
                gap: "8px",
              }}
            >
              <span>{getDisplaySessionName()}</span>
              {sessionName && (
                <Tag size="small" color="green" style={{ fontSize: "10px" }}>
                  Named
                </Tag>
              )}
            </h3>
            <p
              style={{
                margin: "4px 0 0 0",
                color: "#94a3b8",
                fontSize: "12px",
              }}
            >
              Session ID: {sessionId.slice(0, 12)}...
            </p>
          </div>
          {/* Only show rename button for users with action or manage permissions */}
          {currentProject?.permission_level !== "read" && (
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={handleSetSessionName}
              style={{
                color: "#6366f1",
                fontSize: "12px",
                height: "32px",
                padding: "0 12px",
              }}
            >
              Rename
            </Button>
          )}
        </div>
      )}

      {/* Always visible upload area */}
      <div
        className="upload-section"
        style={{
          padding: "16px 20px",
          backgroundColor: "rgba(255, 255, 255, 0.02)",
          position: "sticky",
          top: 0,
          zIndex: 10,
          backdropFilter: "blur(12px)",
        }}
      >
        {/* Only show upload and import for users with action or manage permissions */}
        {currentProject?.permission_level !== "read" && (
          <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
            <div style={{ flex: 2 }}>
              <Dragger
                accept=".csv,.xlsx"
                beforeUpload={handleFileUpload}
                showUploadList={false}
                className="file-upload-area"
                style={{
                  height: "68px",
                  background: "linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.08) 100%)",
                  border: "2px dashed rgba(99, 102, 241, 0.4)",
                  borderRadius: "12px",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  cursor: "pointer",
                  position: "relative",
                  overflow: "hidden",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "linear-gradient(135deg, rgba(99, 102, 241, 0.12) 0%, rgba(139, 92, 246, 0.12) 100%)";
                  e.currentTarget.style.borderColor = "rgba(99, 102, 241, 0.6)";
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 8px 20px rgba(99, 102, 241, 0.2)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.08) 100%)";
                  e.currentTarget.style.borderColor = "rgba(99, 102, 241, 0.4)";
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    gap: "12px",
                    padding: "0 20px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "40px",
                      height: "40px",
                      borderRadius: "10px",
                      background: "linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)",
                      border: "1px solid rgba(99, 102, 241, 0.3)",
                    }}
                  >
                    <UploadOutlined style={{ fontSize: "20px", color: "#818cf8" }} />
                  </div>
                  <div style={{ flex: 1, textAlign: "left" }}>
                    <div
                      style={{
                        color: "#ffffff",
                        fontSize: "14px",
                        fontWeight: "600",
                        marginBottom: "2px",
                        letterSpacing: "0.2px",
                      }}
                    >
                      Drop files here or click to upload
                    </div>
                    <div
                      style={{
                        color: "rgba(255, 255, 255, 0.5)",
                        fontSize: "12px",
                        fontWeight: "400",
                      }}
                    >
                      {selectedScorecards.length > 0 ? (
                        <span>
                          Analyzing with scorecards: <span style={{ color: "#a78bfa", fontWeight: "500" }}>{selectedScorecards.join(", ")}</span>
                        </span>
                      ) : (
                        "CSV or XLSX files supported"
                      )}
                    </div>
                  </div>
                </div>
              </Dragger>
            </div>

            <div style={{ minWidth: "120px" }}>
              <CompactScorecardSelector projectId={currentProject?.id} selectedScorecards={selectedScorecards} onSelectionChange={setSelectedScorecards} />
            </div>
            {/*}
            <div>
              <Tooltip title="Import apps from another session">
                <Button
                  type="primary"
                  icon={importLoading ? <LoadingOutlined /> : <ImportOutlined />}
                  onClick={handleImportApps}
                  loading={importLoading}
                  size="small"
                  style={{
                    backgroundColor: '#10b981',
                    borderColor: '#10b981',
                    height: '50px',
                    minWidth: '100px'
                  }}
                >
                  Import Apps
                </Button>
              </Tooltip>
            </div>
            */}
          </div>
        )}

        {/* Show message for read-only users */}
        {currentProject?.permission_level === "read" && (
          <div
            style={{
              padding: "12px",
              textAlign: "center",
              color: "rgba(255, 255, 255, 0.5)",
              fontSize: "13px",
            }}
          >
            <span>View-only access • Upload and import disabled</span>
          </div>
        )}
      </div>

      {/* Session Reporting Section */}
      {currentProject && sessionId && (
        <div
          style={{
            padding: "16px",
            borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
            backgroundColor: "rgba(255, 255, 255, 0.01)",
            borderRadius: "0 0 8px 8px",
          }}
        >
          <Collapse
            size="small"
            className="report-collapse"
            items={[
              {
                key: "reporting",
                label: (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <BarChartOutlined style={{ color: "#06b6d4" }} />
                      <span style={{ fontWeight: 500, color: "#ffffff" }}>Report</span>
                    </div>
                    {sessionId && (
                      <Button
                        type="primary"
                        icon={<ExportOutlined />}
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent collapse toggle
                          handleExportSession();
                        }}
                        loading={exportLoading}
                        size="small"
                        style={{
                          background: "linear-gradient(135deg, #06b6d4, #0891b2)",
                          border: "none",
                          borderRadius: "6px",
                        }}
                      >
                        Export
                      </Button>
                    )}
                  </div>
                ),
                children: sessionId ? (
                  <div
                    style={{
                      marginTop: 8,
                      maxHeight: "600px",
                      overflowY: "auto",
                      overflowX: "hidden",
                      paddingRight: "4px",
                    }}
                  >
                    {summaryLoading ? (
                      <div style={{ textAlign: "center", padding: "20px", color: "var(--text-secondary)" }}>Loading session summary...</div>
                    ) : sessionSummary ? (
                      <div>{renderScorecardDashboard(sessionSummary)}</div>
                    ) : (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "20px",
                          color: "var(--text-secondary)",
                          fontSize: "14px",
                        }}
                      >
                        No session data available. Upload and analyze files to see reporting.
                      </div>
                    )}
                  </div>
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "20px",
                      color: "var(--text-secondary)",
                      fontSize: "14px",
                    }}
                  >
                    Start a chat session to view reporting data.
                  </div>
                ),
              },
            ]}
          />
        </div>
      )}

      {messages.length === 0 && !sessionId ? (
        <WelcomeScreen />
      ) : (
        <>
          <div className="messages-container">
            {messages.map(renderMessage)}

            {loading && (
              <div className="message assistant">
                <div className="message-avatar">AI</div>
                <div className="message-content">
                  <div className="loading-indicator">
                    <span>Thinking</span>
                    <div className="loading-dots">
                      <div className="loading-dot"></div>
                      <div className="loading-dot"></div>
                      <div className="loading-dot"></div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {uploadingFile && (
            <div className="file-status">
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                <span className="file-name">
                  <FileOutlined style={{ marginRight: "8px" }} />
                  {uploadingFile.name}
                </span>
                <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
                  <div style={{ textAlign: "right" }}>
                    <div className={`status-badge ${uploadingFile.status || "processing"}`}>{uploadingFile.status === "completed" ? "COMPLETED" : uploadingFile.status === "failed" ? "FAILED" : uploadingFile.status === "initializing" ? "INITIALIZING" : uploadingFile.status === "uploaded" ? "UPLOADED" : "PROCESSING"}</div>
                    {uploadingFile.statusText && (
                      <div
                        style={{
                          fontSize: "11px",
                          color: "var(--text-secondary)",
                          marginTop: "2px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {uploadingFile.statusText}
                      </div>
                    )}
                  </div>
                  <Button
                    type="text"
                    size="small"
                    onClick={() => setUploadingFile(null)}
                    style={{
                      minWidth: "auto",
                      width: "24px",
                      height: "24px",
                      padding: 0,
                      opacity: 0.7,
                      color: "var(--text-secondary)",
                    }}
                  >
                    ×
                  </Button>
                </div>
              </div>
              <Progress percent={uploadingFile.progress} showInfo={false} strokeColor="#6366f1" style={{ marginTop: "8px" }} />
            </div>
          )}
        </>
      )}

      <div
        className="chatgpt-container"
        style={{
          padding: "24px",
          textAlign: "center",
          borderTop: "1px solid rgba(255, 255, 255, 0.1)",
          background: "linear-gradient(180deg, rgba(255, 255, 255, 0.02) 0%, rgba(255, 255, 255, 0.04) 100%)",
          backdropFilter: "blur(16px)",
          position: "sticky",
          bottom: 0,
          zIndex: 100,
          boxShadow: "0 -8px 32px rgba(0, 0, 0, 0.2), 0 -2px 8px rgba(0, 0, 0, 0.1)",
        }}
      >
        <div
          style={{
            maxWidth: "560px",
            margin: "0 auto",
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            alignItems: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontSize: "12px",
              color: "rgba(255, 255, 255, 0.65)",
              fontWeight: "600",
              letterSpacing: "1px",
              textTransform: "uppercase",
              marginBottom: "4px",
            }}
          >
            <div
              style={{
                width: "24px",
                height: "2px",
                background: "linear-gradient(90deg, transparent, rgba(16, 163, 127, 0.6))",
                borderRadius: "2px",
              }}
            />
            <span>Enhanced AI Assistant</span>
            <div
              style={{
                width: "24px",
                height: "2px",
                background: "linear-gradient(90deg, rgba(16, 163, 127, 0.6), transparent)",
                borderRadius: "2px",
              }}
            />
          </div>
          <Button
            type="primary"
            size="large"
            onClick={() => {
              window.open("https://chatgpt.com/g/g-6894575997648191b3c88df52d869742-betvictor-liftoff-compliance-cma-test-harness", "_blank");
            }}
            style={{
              background: "linear-gradient(135deg, #10a37f 0%, #1a7f64 50%, #0d9488 100%)",
              border: "1px solid rgba(16, 163, 127, 0.3)",
              borderRadius: "14px",
              fontWeight: "600",
              fontSize: "15px",
              height: "56px",
              paddingLeft: "36px",
              paddingRight: "36px",
              boxShadow: "0 6px 20px rgba(16, 163, 127, 0.3), 0 2px 8px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              position: "relative",
              overflow: "hidden",
              minWidth: "320px",
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = "translateY(-3px) scale(1.03)";
              e.target.style.boxShadow = "0 12px 32px rgba(16, 163, 127, 0.45), 0 6px 16px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.15)";
              e.target.style.background = "linear-gradient(135deg, #0f9c73 0%, #188f69 50%, #0c8a7f 100%)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "translateY(0) scale(1)";
              e.target.style.boxShadow = "0 6px 20px rgba(16, 163, 127, 0.3), 0 2px 8px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)";
              e.target.style.background = "linear-gradient(135deg, #10a37f 0%, #1a7f64 50%, #0d9488 100%)";
            }}
          >
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                justifyContent: "center",
              }}
            >
              <span style={{ fontSize: "20px", lineHeight: 1 }}>🤖</span>
              <span style={{ letterSpacing: "0.3px" }}>App Compliance Research Assistant</span>
            </span>
          </Button>
          <div
            style={{
              fontSize: "12px",
              color: "rgba(255, 255, 255, 0.55)",
              textAlign: "center",
              lineHeight: "1.5",
              fontWeight: "400",
              maxWidth: "420px",
            }}
          >
            Specialized compliance assistant with CMA test harness integration
          </div>
        </div>
      </div>

      {/* Session Name Modal */}
      <Modal
        title="Set Session Name"
        open={isNameModalVisible}
        onCancel={() => {
          setIsNameModalVisible(false);
          nameForm.resetFields();
        }}
        footer={null}
        width={400}
      >
        <Form form={nameForm} onFinish={handleSessionNameSubmit} layout="vertical">
          <Form.Item label="Session Name" name="sessionName" rules={[{ validator: validateSessionName }]} extra="Give your session a memorable name (max 100 characters)">
            <Input placeholder="Enter session name..." maxLength={100} showCount autoFocus />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              <Button
                onClick={() => {
                  setIsNameModalVisible(false);
                  nameForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Save Name
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* Import Apps Modal */}
      <Modal
        title={<span style={{ color: "#fff" }}>Import Apps from Another Session</span>}
        open={isImportModalVisible}
        onCancel={() => {
          setIsImportModalVisible(false);
          importForm.resetFields();
        }}
        footer={null}
        width={600}
        className="dark-modal"
      >
        <Form
          form={importForm}
          onFinish={handleImportSubmit}
          layout="vertical"
          initialValues={{
            overwriteDuplicates: false,
          }}
        >
          <Form.Item name="sourceSessionId" label={<span style={{ color: "#fff" }}>Source Session</span>} rules={[{ required: true, message: "Please select a source session" }]} extra={<span style={{ color: "#94a3b8" }}>Select the session to import apps from</span>}>
            <Select
              placeholder="Choose a session to import from"
              showSearch
              filterOption={(input, option) => option?.label?.toLowerCase().includes(input.toLowerCase())}
              style={{ width: "100%" }}
              options={availableSessions.map((session) => ({
                label: `${session.session_name} (${session.record_count} records)`,
                value: session.session_id,
              }))}
            />
          </Form.Item>

          <Form.Item name="overwriteDuplicates" label={<span style={{ color: "#fff" }}>Duplicate Handling</span>} valuePropName="checked">
            <Switch checkedChildren="Overwrite" unCheckedChildren="Skip" />
          </Form.Item>

          <div
            style={{
              marginBottom: 16,
              fontSize: "14px",
              color: "#94a3b8",
              backgroundColor: "rgba(255, 255, 255, 0.05)",
              padding: "12px",
              borderRadius: "6px",
            }}
          >
            <div style={{ color: "#fff", marginBottom: "8px" }}>
              <strong>Target:</strong> {sessionId ? `${sessionId.slice(0, 12)}...` : "New Compliance Session"}
            </div>
            <div style={{ color: "#fff", marginBottom: "4px" }}>
              <strong>Duplicate Handling:</strong>
            </div>
            <ul style={{ marginTop: 8, paddingLeft: 20, color: "#94a3b8" }}>
              <li>
                <strong>Skip (Default):</strong> Keep existing apps, only add new ones
              </li>
              <li>
                <strong>Overwrite:</strong> Replace existing apps with newer versions
              </li>
            </ul>
          </div>

          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button
                onClick={() => {
                  setIsImportModalVisible(false);
                  importForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={importLoading} icon={<ImportOutlined />}>
                Import Apps
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <AppActionsHistory sessionId={sessionId} projectId={currentProject?.id} visible={isActionsHistoryVisible} onClose={() => setIsActionsHistoryVisible(false)} />
    </div>
  );
};

export default ChatInterface;